/**
 * Canonical germination soil temperatures (2-inch depth) for lawn grasses.
 * Single source of truth for /tools/soil-temperature: the germination chart
 * component, the server-rendered species table, and the FAQ answer all read
 * from these rows. Do not restate these numbers elsewhere on the page.
 */

export interface GerminationRow {
  grass: string
  season: "Cool" | "Warm"
  minF: number
  optimalRangeF: string
  daysToGerminate: string
  seedingWindow: string
  note?: string
  // Jonathan Green where they make a pure SKU; closest brand-name pure-seed bag otherwise.
  affiliateAsin?: string
}

export const GERMINATION_ROWS: GerminationRow[] = [
  // Sorted by minF ascending so the table tells a story top-to-bottom
  { grass: "Annual Ryegrass", season: "Cool", minF: 40, optimalRangeF: "50–65°F", daysToGerminate: "5–10", seedingWindow: "Fall (overseeding)", affiliateAsin: "B000FBKV26" }, // JG Fast Grow 7 lb
  { grass: "Perennial Ryegrass", season: "Cool", minF: 45, optimalRangeF: "50–65°F", daysToGerminate: "5–10", seedingWindow: "Early fall or spring", affiliateAsin: "B000BQ824Y" }, // JG Touch-Up TRI-RYE 7 lb
  { grass: "Tall Fescue", season: "Cool", minF: 50, optimalRangeF: "60–75°F", daysToGerminate: "7–14", seedingWindow: "Early fall", affiliateAsin: "B0FMLC9RF8" }, // JG Black Beauty Founders' Reserve Tall Fescue 3 lb
  { grass: "Fine Fescue", season: "Cool", minF: 50, optimalRangeF: "60–75°F", daysToGerminate: "7–21", seedingWindow: "Early fall", affiliateAsin: "B01M1IPK1Z" }, // JG Black Beauty Dense Shade 3 lb (fine-fescue dominant)
  { grass: "Kentucky Bluegrass", season: "Cool", minF: 55, optimalRangeF: "60–70°F", daysToGerminate: "14–30", seedingWindow: "Early fall (spring second-best)", affiliateAsin: "B003AYR3FI" }, // JG Blue Panther KBG 3 lb
  { grass: "Buffalograss", season: "Warm", minF: 60, optimalRangeF: "70–85°F", daysToGerminate: "14–30", seedingWindow: "Late spring" },
  { grass: "Bermudagrass", season: "Warm", minF: 65, optimalRangeF: "70–95°F", daysToGerminate: "10–30", seedingWindow: "Late spring to early summer", affiliateAsin: "B0GNCDNB75" }, // JG Blue Panther Bermudagrass 4 lb
  { grass: "Bahia", season: "Warm", minF: 65, optimalRangeF: "70–95°F", daysToGerminate: "14–28", seedingWindow: "Late spring to early summer", affiliateAsin: "B01NAC0PS1" }, // SeedRanch Pensacola Bahia 10 lb (JG doesn't make pure bahia)
  { grass: "Centipede", season: "Warm", minF: 70, optimalRangeF: "75–85°F", daysToGerminate: "14–21", seedingWindow: "Late spring", affiliateAsin: "B00ARYORQC" }, // Scotts Turf Builder Centipede 5 lb (JG doesn't make centipede)
  { grass: "Zoysia", season: "Warm", minF: 70, optimalRangeF: "75–90°F", daysToGerminate: "14–21", seedingWindow: "Late spring to early summer", affiliateAsin: "B00ARYRY1W" }, // Scotts Turf Builder Zoysia 5 lb (JG doesn't make zoysia)
  {
    grass: "Kikuyu",
    season: "Warm",
    minF: 70,
    optimalRangeF: "70–85°F",
    daysToGerminate: "–",
    seedingWindow: "Late spring (sod/stolons)",
    note: "Sod/stolons in practice; seed is a niche specialty",
  },
  {
    grass: "St. Augustine",
    season: "Warm",
    minF: 70,
    optimalRangeF: "75–90°F",
    daysToGerminate: "–",
    seedingWindow: "Late spring to summer (plugs/sod)",
    note: "Plugs/sod only, not commonly seeded",
  },
]

/** "55°F min, optimal 60–70°F" — the one string format both tables and the FAQ use. */
export function formatGerminationTemp(row: GerminationRow): string {
  return `${row.minF}°F min, optimal ${row.optimalRangeF}`
}
